<h2 mat-dialog-title>
  {{ isEditMode ? 'Edit Constraint' : 'Add Constraint' }}
</h2>

<!-- Edit mode: show form directly -->
<ng-container *ngIf="isEditMode">
  <form (ngSubmit)="onSubmit()" #constraintForm="ngForm" autocomplete="off">
    <mat-dialog-content>
      <mat-form-field appearance="outline" style="width: 100%;">
        <mat-label>Human Readable Definition</mat-label>
        <textarea matInput name="humanReadableDefinition" [(ngModel)]="data.humanReadableDefinition" rows="5" required></textarea>
        <mat-hint>A clear description of the constraint in plain language</mat-hint>
      </mat-form-field>

      <mat-form-field appearance="outline" style="width: 100%; margin-top: 16px;">
        <mat-label>Constraint Type</mat-label>
        <mat-select name="type" [(ngModel)]="data.type" required>
          <mat-option value="hard">Hard (Must be satisfied)</mat-option>
          <mat-option value="soft">Soft (Preference)</mat-option>
        </mat-select>
        <mat-hint>Hard constraints must be satisfied, soft constraints are preferences</mat-hint>
      </mat-form-field>

      <mat-form-field appearance="outline" style="width: 100%; margin-top: 16px;" *ngIf="data.type === 'soft'">
        <mat-label>Weight</mat-label>
        <input matInput type="number" name="weight" [(ngModel)]="data.weight" min="1" required>
        <mat-hint>Weight for soft constraints (1+). Higher values = stronger preferences</mat-hint>
      </mat-form-field>

      <div style="margin-top: 20px;">
        <mat-slide-toggle
          name="active"
          [(ngModel)]="data.active"
          color="primary">
          Active
        </mat-slide-toggle>
        <p style="margin: 8px 0 0 0; color: #666; font-size: 12px;">
          When inactive, this constraint will be excluded from scheduling calculations
        </p>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close type="button">Cancel</button>
      <button
        mat-flat-button
        color="primary"
        type="submit"
        [disabled]="!data.humanReadableDefinition || !data.type"
      >
        Save
      </button>
    </mat-dialog-actions>
  </form>
</ng-container>

<!-- Add mode: show tabs -->
<ng-container *ngIf="!isEditMode">
  <mat-tab-group [(selectedIndex)]="selectedTabIndex" animationDuration="200ms">
    <!-- Custom Tab -->
    <mat-tab label="Custom">
      <form (ngSubmit)="onSubmit()" #constraintForm="ngForm" autocomplete="off" style="padding-top: 16px;">
        <mat-dialog-content>
          <mat-form-field appearance="outline" style="width: 100%;">
            <mat-label>Human Readable Definition</mat-label>
            <textarea matInput name="humanReadableDefinition" [(ngModel)]="data.humanReadableDefinition" rows="5" required></textarea>
            <mat-hint>A clear description of the constraint in plain language. AI will generate the code.</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" style="width: 100%; margin-top: 16px;">
            <mat-label>Constraint Type</mat-label>
            <mat-select name="type" [(ngModel)]="data.type" required>
              <mat-option value="hard">Hard (Must be satisfied)</mat-option>
              <mat-option value="soft">Soft (Preference)</mat-option>
            </mat-select>
            <mat-hint>Hard constraints must be satisfied, soft constraints are preferences</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" style="width: 100%; margin-top: 16px;" *ngIf="data.type === 'soft'">
            <mat-label>Weight</mat-label>
            <input matInput type="number" name="weight" [(ngModel)]="data.weight" min="1" required>
            <mat-hint>Weight for soft constraints (1+). Higher values = stronger preferences</mat-hint>
          </mat-form-field>

          <div style="margin-top: 20px;">
            <mat-slide-toggle
              name="active"
              [(ngModel)]="data.active"
              color="primary">
              Active
            </mat-slide-toggle>
            <p style="margin: 8px 0 0 0; color: #666; font-size: 12px;">
              When inactive, this constraint will be excluded from scheduling calculations
            </p>
          </div>
        </mat-dialog-content>
        <mat-dialog-actions align="end">
          <button mat-button mat-dialog-close type="button">Cancel</button>
          <button
            mat-flat-button
            color="primary"
            type="submit"
            [disabled]="!data.humanReadableDefinition || !data.type"
          >
            Add Custom Constraint
          </button>
        </mat-dialog-actions>
      </form>
    </mat-tab>

    <!-- Library Tab -->
    <mat-tab label="Library">
      <div style="padding-top: 16px;">
        <mat-dialog-content>
          <!-- Loading state -->
          <div *ngIf="loadingLibrary" style="display: flex; justify-content: center; padding: 40px;">
            <mat-spinner diameter="40"></mat-spinner>
          </div>

          <!-- Empty state -->
          <div *ngIf="!loadingLibrary && libraryConstraints.length === 0" style="text-align: center; padding: 40px; color: #666;">
            <p>No library constraints available yet.</p>
          </div>

          <!-- Library constraints grouped by category -->
          <div *ngIf="!loadingLibrary && libraryConstraints.length > 0">
            <div *ngFor="let category of categories" style="margin-bottom: 24px;">
              <h4 style="margin: 0 0 12px 0; color: #666; text-transform: uppercase; font-size: 12px; letter-spacing: 0.5px;">
                {{ getCategoryLabel(category) }}
              </h4>

              <div style="display: flex; flex-direction: column; gap: 8px;">
                <mat-card
                  *ngFor="let constraint of libraryByCategory[category]"
                  [class.selected]="constraint | equals:selectedLibraryConstraint:'id'"
                  (click)="selectLibraryConstraint(constraint)"
                  style="cursor: pointer; transition: all 0.2s;"
                  [style.border]="(constraint | equals:selectedLibraryConstraint:'id') ? '2px solid #1976d2' : '1px solid #e0e0e0'"
                  [style.background]="(constraint | equals:selectedLibraryConstraint:'id') ? '#e3f2fd' : ((libraryStatus | getProperty:constraint.slug + '.added':false) ? '#f5f5f5' : 'white')"
                  [style.opacity]="(libraryStatus | getProperty:constraint.slug + '.added':false) && !(libraryStatus | getProperty:constraint.slug + '.hasUpdate':false) ? '0.7' : '1'"
                >
                  <mat-card-content style="padding: 12px 16px;">
                    <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                      <div style="flex: 1;">
                        <div style="font-weight: 500; margin-bottom: 4px;">{{ constraint.name }}</div>
                        <div style="font-size: 13px; color: #666;">{{ constraint.description }}</div>
                      </div>
                      <mat-chip-set style="margin-left: 12px; display: flex; gap: 4px;">
                        <mat-chip [highlighted]="constraint.type === 'hard'" [color]="constraint.type === 'hard' ? 'primary' : 'accent'">
                          {{ constraint.type === 'hard' ? 'Hard' : 'Soft' }}
                        </mat-chip>
                        <mat-chip *ngIf="(libraryStatus | getProperty:constraint.slug + '.added':false) && !(libraryStatus | getProperty:constraint.slug + '.hasUpdate':false)" style="background: #4caf50; color: white;">
                          Added
                        </mat-chip>
                        <mat-chip *ngIf="libraryStatus | getProperty:constraint.slug + '.hasUpdate':false" style="background: #ff9800; color: white;">
                          Update Available
                        </mat-chip>
                      </mat-chip-set>
                    </div>
                  </mat-card-content>
                </mat-card>
              </div>
            </div>

            <!-- Weight adjustment for selected soft constraint -->
            <div *ngIf="selectedLibraryConstraint && selectedLibraryConstraint.type === 'soft'" style="margin-top: 16px; padding: 16px; background: #f5f5f5; border-radius: 8px;">
              <mat-form-field appearance="outline" style="width: 100%;">
                <mat-label>Weight</mat-label>
                <input matInput type="number" [(ngModel)]="libraryWeight" min="1" max="10000">
                <mat-hint>Adjust the weight for this soft constraint (default: {{ selectedLibraryConstraint.defaultWeight }})</mat-hint>
              </mat-form-field>
            </div>
          </div>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
          <button mat-button mat-dialog-close type="button">Cancel</button>
          <button
            mat-flat-button
            [color]="selectedLibraryConstraint && (libraryStatus | getProperty:selectedLibraryConstraint.slug + '.hasUpdate':false) ? 'warn' : 'primary'"
            (click)="onLibraryAction()"
            [disabled]="isButtonDisabled()"
          >
            {{ getButtonLabel() }}
          </button>
        </mat-dialog-actions>
      </div>
    </mat-tab>
  </mat-tab-group>
</ng-container>